import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Box, Button, CircularProgress } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircleOutline,
  DashboardOutlined,
  DescriptionOutlined,
  RecordVoiceOverOutlined,
  SettingsOutlined
} from '@mui/icons-material';

import InitialDetails from './AssistantForms/InitialDetails';
import TranscriberAndAudio from './AssistantForms/TranscriberAndAudio';
import AdditionalConfigurations from './AssistantForms/AdditionalConfigurations';
import Actions from './AssistantForms/Actions';
import Loader from './Loader';
import api from "../api";
import { useGetAllPermissions } from '../pages/utils/getAllPermissions';
import NewPrompt from './AssistantForms/NewPrompt';
import { PublishButtonSx } from '../styles/GlobalStyles';
import TooltipHover from './TooltipHover';

const TABS_CONFIG = [
  { id: 'information', label: 'Information', icon: <DashboardOutlined sx={{ fontSize: 20 }} />, component: InitialDetails, requiredPermissions: [] },
  { id: 'prompt', label: 'Prompt', icon: <DescriptionOutlined sx={{ fontSize: 20 }} />, component: NewPrompt, requiredPermissions: ['fetch_llm_model', 'fetch_LLM_providers'] },
  { id: 'transcriber', label: 'Transcriber and Voice', icon: <RecordVoiceOverOutlined sx={{ fontSize: 20 }} />, component: TranscriberAndAudio, requiredPermissions: ['get_voice_providers', 'fetch_VoiceSynthesizers_model', 'fetch_voicelanguage_by_synthesizerid', 'fetch_language_by_transcriberid', 'fetch_transcribers_provider'] },
  { id: 'configurations', label: 'Configurations', icon: <SettingsOutlined sx={{ fontSize: 20 }} />, component: AdditionalConfigurations, requiredPermissions: [] },
  { id: 'actions', label: 'Actions', icon: <CheckCircleOutline sx={{ fontSize: 20 }} />, component: Actions, requiredPermissions: ['create_action'] }
];

const NewAssistantForm = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(false);
  const [assistantData, setAssistantData] = useState(null);

  const navigate = useNavigate();
  const { company_id } = useParams();
  const location = useLocation();
  const { permissions, permissionsLoading, getAllPermissions } = useGetAllPermissions();
  
  // Check if coming from template
  const isFromTemplate = location.pathname.includes('/template');

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("User"));
        setUserData(user);
        
        // Load assistant data from localStorage
        const assistantFromStorage = JSON.parse(localStorage.getItem("assistant"));
        setAssistantData(assistantFromStorage);
        
        await getAllPermissions();
        setIsInitialized(true);
        setError(validateAssistantData());
      } catch (error) {
        toast.error('Failed to initialize component');
      }
    };
    initializeComponent();
  }, [getAllPermissions]);

  useEffect(() => {
    const interval = setInterval(() => {
      setError(validateAssistantData());
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTab]);

 const validateAssistantData = () => {
    try {
      const assistant = JSON.parse(localStorage.getItem("assistant"));
      const requiredFields = ["Name", "Category", "Description", "Type", "Prompt"];

      if (!assistant) {
        console.error("Assistant data is missing. Required fields:", requiredFields.join(", "));
        return true;
      }

      const missingFields = requiredFields.filter(field => !assistant[field]);

      if (missingFields.length > 0) {
        console.error("Missing fields:", missingFields.join(", "));
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error parsing assistant data from localStorage:", error.message);
      return true;
    }
  };



  const convertBase64ToBlob = useCallback((base64Data) => {
    if (!base64Data) return null;
    const byteString = atob(base64Data.content.split(',')[1]);
    const mimeString = base64Data.content.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    return new File([new Blob([ab], { type: mimeString })], base64Data.name, { type: base64Data.type });
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const assistant = JSON.parse(localStorage.getItem("assistant"));
      const formData = new FormData();
      
      // Map properties to expected API format
      const apiMapping = {
        "name": "Name",
        "description": "Description", 
        "type": "Type",
        "category": "Category",
        "Prompt": "Prompt",
        // Map other fields as needed
      };
      
      Object.entries(assistant).forEach(([key, value]) => {
        const apiKey = apiMapping[key] || key;
        formData.append(apiKey, value);
      });
      
      formData.append("Organization", company_id || localStorage.getItem("Organization"));

      const logoFile = convertBase64ToBlob(JSON.parse(localStorage.getItem("Logo")));
      const knowledgeBaseData = JSON.parse(localStorage.getItem("KnowledgeBase"));

      if (logoFile) formData.append("logo", logoFile);
      if (knowledgeBaseData) {
        formData.append("knowledgeBaseName", knowledgeBaseData.name);
        formData.append("knowledgeBaseSize", knowledgeBaseData.size);
        formData.append("knowledgeBaseType", knowledgeBaseData.type);
        formData.append("knowledgeBaseUrl", knowledgeBaseData.url);
      } else if (assistantData?.knowledgeBaseUrl === null) {
        formData.delete("knowledgeBaseName");
        formData.delete("knowledgeBaseSize");
        formData.delete("knowledgeBaseType");
        formData.delete("knowledgeBaseUrl");
      }

      const { data } = await api.post("/v1/assistant/create", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${userData?.Token}`
        }
      });

      toast.success(data.message);
      const actions = JSON.parse(localStorage.getItem('actions') || '[]');
      await Promise.all(actions.filter(a => !a._id).map(action =>
        api.post("/v1/actions/create-action", { ...action, Assistant: data.assistant._id }, {
          headers: { Authorization: `Bearer ${userData?.Token}` }
        })
      ));

      navigate(userData?.Role === "SuperAdmin" ? "/assistants" : `/company/${company_id}/assistants`);

    } catch (error) {
      toast.error(error?.response?.data?.details || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableTabs = TABS_CONFIG.filter(tab =>
    tab.requiredPermissions.length === 0 || tab.requiredPermissions.every(p => permissions?.includes(p))
  );

  if (permissionsLoading || !isInitialized) return <Loader />;

  return (
    <Box sx={{ width: '100%', padding: '24px' }}>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', marginTop: '0.5rem' }}>
        <Box sx={{ display: "flex", backgroundColor: "#FFFFFF", border: '1px solid #E5E7EB', borderRadius: "12px", px:"0.5rem",py:"0.5rem", gap: "0.5rem" }}>
          {availableTabs.map((tab, index) => (
            <Typography
              key={index}
              onClick={() => setActiveTab(index)}
              sx={{
                cursor: "pointer",
                padding: "0.5rem 1rem",
                borderRadius: activeTab === index ? "4px" : "8px",
                fontWeight: 500,
                backgroundColor: activeTab === index ? "#02042D" : "transparent",
                color: activeTab === index ? "white" : "black",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                '&:hover': {
                  backgroundColor: activeTab === index ? "#02042D" : "#EDEDED",
                  color: activeTab === index ? "white" : "black",
                  borderRadius: "4px",
                }
              }}
            >
              {tab.icon}
              {tab.label}
            </Typography>
          ))}
        </Box>
        <TooltipHover title="Please fill out all required fields first" show={error}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={error || isSubmitting}
            sx={PublishButtonSx}
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : "Create Assistant"}
          </Button>
        </TooltipHover>
      </Box>

      {availableTabs[activeTab]?.component && assistantData && 
        React.createElement(availableTabs[activeTab].component, {
          assistantId: null,
          initialData: assistantData,
          isFromTemplate: isFromTemplate 
        })}

    </Box>
  );
};

export default NewAssistantForm;
